import apiClient from '@/lib/apiClient';
import { buildListParams, extractPaginatedData } from '@/shared/utils/queryUtils';
import type { ApiSuccessResponse } from '@/shared/types';

export type AnalyticsEvent = {
  id: number;
  eventName: string;
  eventId?: string | null;
  userId?: number | null;
  userName?: string | null;
  userEmail?: string | null;
  userPhone?: string | null;
  anonymousId?: string | null;
  sessionId?: string | null;
  screenName?: string | null;
  entityType?: string | null;
  entityId?: number | null;
  targetUserId?: number | null;
  targetUserName?: string | null;
  targetAdTitle?: string | null;
  cityId?: number | null;
  districtId?: number | null;
  platform?: string | null;
  appVersion?: string | null;
  appsflyerId?: string | null;
  mediaSource?: string | null;
  campaign?: string | null;
  adset?: string | null;
  ad?: string | null;
  installTime?: string | null;
  deepLinkValue?: string | null;
  isRetargeting?: boolean | null;
  metadata?: Record<string, unknown>;
  occurredAt?: string | null;
  createdAt?: string | null;
};

export type GrowthAnalyticsParams = {
  from?: string;
  to?: string;
  platform?: string;
  cityId?: number | string;
  districtId?: number | string;
  adType?: 'room_ad' | 'room_wanted' | '';
};

export type GrowthAnalyticsResponse = Record<string, any>;

export type AnalyticsGeoOption = {
  id: number;
  name: string;
};

export type AnalyticsEventsSummary = {
  totalMatchingEvents: number;
  sampledEvents: number;
  topUsers: Array<{
    userId: number;
    userName?: string | null;
    userEmail?: string | null;
    userPhone?: string | null;
    totalEvents: number;
    contactCount: number;
    searchCount: number;
    viewCount: number;
    lastSeenAt?: string | null;
  }>;
};

function cleanParams(params: GrowthAnalyticsParams = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  );
}

function extractData<T>(response: { data?: ApiSuccessResponse<T> }): T {
  return response.data?.data as T;
}

export const analyticsApi = {
  listEvents: async (params: any) => {
    const queryParams = buildListParams(params);
    const res = await apiClient.get('/admin/analytics/events', { params: queryParams });
    return extractPaginatedData<AnalyticsEvent>(res);
  },
  eventsSummary: async (params: any = {}) => {
    const queryParams = buildListParams(params);
    delete queryParams.page;
    delete queryParams.size;
    const res = await apiClient.get('/admin/analytics/events/summary', { params: queryParams });
    return extractData<AnalyticsEventsSummary>(res);
  },
  growthOverview: async (params: GrowthAnalyticsParams = {}) => {
    const res = await apiClient.get('/admin/analytics/growth/overview', { params: cleanParams(params) });
    return extractData<GrowthAnalyticsResponse>(res);
  },
  growthFunnels: async (params: GrowthAnalyticsParams = {}) => {
    const res = await apiClient.get('/admin/analytics/growth/funnels', { params: cleanParams(params) });
    return extractData<GrowthAnalyticsResponse>(res);
  },
  growthSearch: async (params: GrowthAnalyticsParams = {}) => {
    const res = await apiClient.get('/admin/analytics/growth/search', { params: cleanParams(params) });
    return extractData<GrowthAnalyticsResponse>(res);
  },
  growthMarketplace: async (params: GrowthAnalyticsParams = {}) => {
    const res = await apiClient.get('/admin/analytics/growth/marketplace', { params: cleanParams(params) });
    return extractData<GrowthAnalyticsResponse>(res);
  },
  growthAttribution: async (params: GrowthAnalyticsParams = {}) => {
    const res = await apiClient.get('/admin/analytics/growth/attribution', { params: cleanParams(params) });
    return extractData<GrowthAnalyticsResponse>(res);
  },
  growthRetention: async (params: GrowthAnalyticsParams = {}) => {
    const res = await apiClient.get('/admin/analytics/growth/retention', { params: cleanParams(params) });
    return extractData<GrowthAnalyticsResponse>(res);
  },
  demandIntelligence: async (params: GrowthAnalyticsParams = {}) => {
    const res = await apiClient.get('/admin/analytics/growth/demand-intelligence', { params: cleanParams(params) });
    return extractData<GrowthAnalyticsResponse>(res);
  },
  geoCities: async () => {
    const res = await apiClient.get('/admin/analytics/growth/geo/cities');
    return extractData<AnalyticsGeoOption[]>(res);
  },
  geoDistricts: async (cityId?: number | string | null) => {
    const res = await apiClient.get('/admin/analytics/growth/geo/districts', { params: cleanParams({ cityId: cityId ?? '' }) });
    return extractData<AnalyticsGeoOption[]>(res);
  },
};
